import { useState } from 'react'
import { Modal, Button, Card, Row, Col, Badge } from 'react-bootstrap'
import {
    EMAIL_STARTERS,
    SMS_STARTERS,
    StarterLocale,
    TemplateStarter,
    resolveStarter,
    starterHasLocale,
} from '../../../constants/templateStarters'
import { useIntl, FormattedMessage } from 'react-intl'
import { useEmailPreviewLogo } from '../../../helpers/emailLogo'
import {
    TEMPLATE_MESSAGES as M,
    STARTER_MESSAGES,
    ICON_CHAT,
    ICON_ENVELOPE,
    LANG_CODE_EN,
    LANG_CODE_ES,
    VARIABLE_EXAMPLE,
    withColon,
    withIcon,
} from '../templateMessages'

type Props = {
    show: boolean
    onHide: () => void
    onSelect: (starter: TemplateStarter) => void
    onStartFromScratch: () => void
    defaultChannel?: 'EMAIL' | 'SMS'
}

const SMS_CHAR_LIMIT = 160
const CONTENT_LOCALES: StarterLocale[] = ['en', 'es']

export const TemplateStarterModal = ({ show, onHide, onSelect, onStartFromScratch, defaultChannel }: Props) => {
    const intl = useIntl()
    const withPreviewLogo = useEmailPreviewLogo()
    /** Nombre/descripción traducidos del starter; el EN del constante es el respaldo. */
    const starterText = (id: string, field: 'name' | 'description', fallback: string) => {
        const message = STARTER_MESSAGES[`${id}.${field}` as keyof typeof STARTER_MESSAGES]
        return message ? intl.formatMessage(message) : fallback
    }
    const [activeChannel, setActiveChannel] = useState<'EMAIL' | 'SMS'>(defaultChannel ?? 'EMAIL')
    const [previewStarter, setPreviewStarter] = useState<TemplateStarter | null>(null)

    // Idioma del *contenido* de la plantilla (distinto del idioma de la interfaz).
    // Arranca en el idioma de la UI y se puede cambiar en bloque o starter por starter.
    const uiLocale: StarterLocale = intl.locale.toLowerCase().startsWith('es') ? 'es' : 'en'
    const [defaultLocale, setDefaultLocale] = useState<StarterLocale>(uiLocale)
    const [localeByStarter, setLocaleByStarter] = useState<Record<string, StarterLocale>>({})

    const localeFor = (starter: TemplateStarter): StarterLocale => {
        const locale = localeByStarter[starter.id] ?? defaultLocale
        return starterHasLocale(starter, locale) ? locale : 'en'
    }
    const setAllLocales = (locale: StarterLocale) => {
        setDefaultLocale(locale)
        setLocaleByStarter({})
    }
    const setStarterLocale = (id: string, locale: StarterLocale) =>
        setLocaleByStarter((prev) => ({ ...prev, [id]: locale }))

    const localeLabel = (locale: StarterLocale) =>
        intl.formatMessage(locale === 'es' ? M.langSpanish : M.langEnglish)

    /**
     * Nombre/descripción de la tarjeta en el idioma elegido para el contenido: si
     * eliges la variante en español, la tarjeta muestra el título en español.
     * Con la variante en inglés se usa la traducción de la interfaz.
     */
    const starterLabel = (starter: TemplateStarter, field: 'name' | 'description') => {
        const locale = localeFor(starter)
        if (locale !== 'en') return resolveStarter(starter, locale)[field]
        return starterText(starter.id, field, starter[field])
    }

    /** Interruptor EN/ES de una tarjeta. `stopPropagation` para no seleccionar el starter. */
    const renderLocaleToggle = (starter: TemplateStarter, size: 'sm' | 'xs' = 'xs') => (
        <div
            className='btn-group btn-group-sm flex-shrink-0'
            role='group'
            aria-label={intl.formatMessage(M.starterContentLanguage)}
            onClick={(e) => e.stopPropagation()}
        >
            {CONTENT_LOCALES.filter((locale) => starterHasLocale(starter, locale)).map((locale) => {
                const active = localeFor(starter) === locale
                return (
                    <button
                        key={locale}
                        type='button'
                        className={`btn ${active ? 'btn-secondary' : 'btn-outline-secondary'} py-0 px-2`}
                        style={{ fontSize: size === 'xs' ? 11 : 12, lineHeight: 1.7 }}
                        title={localeLabel(locale)}
                        aria-label={localeLabel(locale)}
                        aria-pressed={active}
                        onClick={() => setStarterLocale(starter.id, locale)}
                    >
                        {locale === 'es' ? LANG_CODE_ES : LANG_CODE_EN}
                    </button>
                )
            })}
        </div>
    )

    const handleSelect = (starter: TemplateStarter) => {
        const locale = localeFor(starter)
        setPreviewStarter(null)
        onSelect(resolveStarter(starter, locale))
    }

    const previewResolved = previewStarter ? resolveStarter(previewStarter, localeFor(previewStarter)) : null

    return (
        <>
        <Modal show={show && !previewStarter} onHide={onHide} size='xl' centered>
            <Modal.Header closeButton>
                <Modal.Title><FormattedMessage {...M.starterTitle} /></Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className='text-muted mb-4'>
                    <FormattedMessage {...M.starterSubtitle} />
                </p>

                {/* Channel Tabs + content language */}
                <div className='d-flex gap-2 mb-4 border-bottom pb-3 align-items-center flex-wrap'>
                    <button
                        type='button'
                        className={`btn btn-sm px-4 ${activeChannel === 'EMAIL' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveChannel('EMAIL')}
                    >
                        {withIcon(ICON_ENVELOPE, intl.formatMessage(M.starterEmailTab))}
                        <Badge bg='light' text='dark' className='ms-2'>{EMAIL_STARTERS.length}</Badge>
                    </button>
                    <button
                        type='button'
                        className={`btn btn-sm px-4 ${activeChannel === 'SMS' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveChannel('SMS')}
                    >
                        {withIcon(ICON_CHAT, intl.formatMessage(M.starterSmsTab))}
                        <Badge bg='light' text='dark' className='ms-2'>{SMS_STARTERS.length}</Badge>
                    </button>

                    <div className='ms-auto d-flex align-items-center gap-2'>
                        <span className='text-muted small'>
                            <FormattedMessage {...M.starterContentLanguage} />
                        </span>
                        <div
                            className='btn-group btn-group-sm'
                            role='group'
                            aria-label={intl.formatMessage(M.starterContentLanguage)}
                        >
                            {CONTENT_LOCALES.map((locale) => (
                                <button
                                    key={locale}
                                    type='button'
                                    className={`btn ${defaultLocale === locale ? 'btn-dark' : 'btn-outline-secondary'}`}
                                    aria-pressed={defaultLocale === locale}
                                    onClick={() => setAllLocales(locale)}
                                >
                                    {localeLabel(locale)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Email starters grid */}
                {activeChannel === 'EMAIL' && (
                    <Row className='g-3 mb-4'>
                        {EMAIL_STARTERS.map((starter) => (
                            <Col key={starter.id} md={4}>
                                <Card
                                    className='h-100'
                                    style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                    onClick={() => handleSelect(starter)}
                                    onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                                >
                                    <Card.Body className='d-flex flex-column'>
                                        <div className='d-flex justify-content-end'>
                                            {renderLocaleToggle(starter)}
                                        </div>
                                        <div className='text-center mb-3'>
                                            <div style={{ fontSize: 44 }}>{starter.icon}</div>
                                        </div>
                                        <Card.Title className='text-center mb-1' style={{ fontSize: 15 }}>
                                            {starterLabel(starter, 'name')}
                                        </Card.Title>
                                        <Card.Text className='text-muted text-center flex-grow-1' style={{ fontSize: 13 }}>
                                            {starterLabel(starter, 'description')}
                                        </Card.Text>
                                        <div className='d-flex gap-2 mt-3'>
                                            <Button
                                                variant='outline-secondary'
                                                size='sm'
                                                className='flex-shrink-0'
                                                onClick={(e) => { e.stopPropagation(); setPreviewStarter(starter) }}
                                            >
                                                <i className='bi bi-eye'></i>
                                            </Button>
                                            <Button variant='primary' size='sm' className='flex-grow-1'>
                                                <FormattedMessage {...M.starterUseTemplate} />
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* SMS starters list */}
                {activeChannel === 'SMS' && (
                    <div className='mb-4'>
                        <div className='alert alert-info py-2 small mb-3'>
                            <i className='bi bi-info-circle me-2'></i>
                            <FormattedMessage
                                {...M.starterSmsHint}
                                values={{
                                    limit: SMS_CHAR_LIMIT,
                                    variable: <code>{VARIABLE_EXAMPLE}</code>,
                                    b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
                                }}
                            />
                        </div>
                        <Row className='g-3'>
                            {SMS_STARTERS.map((starter) => {
                                const resolved = resolveStarter(starter, localeFor(starter))
                                const charCount = resolved.content.bodyText.length
                                const segments = Math.ceil(charCount / SMS_CHAR_LIMIT)
                                return (
                                    <Col key={starter.id} md={6}>
                                        <Card
                                            className='h-100'
                                            style={{ cursor: 'pointer', transition: 'all 0.15s' }}
                                            onClick={() => handleSelect(starter)}
                                            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.12)')}
                                            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '')}
                                        >
                                            <Card.Body>
                                                <div className='d-flex align-items-start gap-3 mb-3'>
                                                    <span style={{ fontSize: 32 }}>{starter.icon}</span>
                                                    <div className='flex-grow-1'>
                                                        <div className='fw-semibold' style={{ fontSize: 14 }}>
                                                            {starterLabel(starter, 'name')}
                                                        </div>
                                                        <div className='text-muted' style={{ fontSize: 12 }}>
                                                            {starterLabel(starter, 'description')}
                                                        </div>
                                                    </div>
                                                    <div className='d-flex flex-column align-items-end gap-2'>
                                                        <Badge bg={segments > 1 ? 'warning' : 'success'} text={segments > 1 ? 'dark' : undefined}>
                                                            <FormattedMessage
                                                                {...M.starterCharsSegments}
                                                                values={{chars: charCount, segments}}
                                                            />
                                                        </Badge>
                                                        {renderLocaleToggle(starter)}
                                                    </div>
                                                </div>
                                                {/* SMS preview bubble */}
                                                <div
                                                    className='rounded p-3 mb-3'
                                                    style={{ background: '#e8f4fd', fontSize: 13, lineHeight: 1.5, color: '#333', fontFamily: 'system-ui, sans-serif' }}
                                                >
                                                    {resolved.content.bodyText}
                                                </div>
                                                <Button variant='success' size='sm' className='w-100'>
                                                    <FormattedMessage {...M.starterUseTemplate} />
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                )
                            })}
                        </Row>
                    </div>
                )}

                <div className='text-center pt-3 border-top'>
                    <Button variant='outline-secondary' onClick={onStartFromScratch}>
                        <i className='bi bi-file-earmark-plus me-2'></i>
                        <FormattedMessage {...M.starterFromScratch} />
                    </Button>
                </div>
            </Modal.Body>
        </Modal>

        {/* Email HTML preview modal */}
        {previewStarter && previewResolved && (
            <Modal show onHide={() => setPreviewStarter(null)} size='lg' centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fs-6'>
                        <span style={{ fontSize: 20 }} className='me-2'>{previewStarter.icon}</span>
                        {starterLabel(previewStarter, 'name')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='p-0'>
                    {previewResolved.content.subject && (
                        <div className='px-4 py-3 border-bottom bg-light small'>
                            <span className='text-muted me-2'>{withColon(intl.formatMessage(M.subject))}</span>
                            <span className='fw-semibold'>{previewResolved.content.subject}</span>
                        </div>
                    )}
                    <div
                        style={{ maxHeight: '65vh', overflowY: 'auto' }}
                        dangerouslySetInnerHTML={{ __html: withPreviewLogo(previewResolved.content.bodyHtml ?? previewResolved.content.bodyText) }}
                    />
                </Modal.Body>
                <Modal.Footer className='py-2 gap-2'>
                    <div className='me-auto d-flex align-items-center gap-2'>
                        <span className='text-muted small'>
                            <FormattedMessage {...M.starterContentLanguage} />
                        </span>
                        {renderLocaleToggle(previewStarter, 'sm')}
                    </div>
                    <Button variant='outline-secondary' size='sm' onClick={() => setPreviewStarter(null)}>
                        <FormattedMessage {...M.back} />
                    </Button>
                    <Button variant='primary' size='sm' onClick={() => handleSelect(previewStarter)}>
                        <i className='bi bi-check2 me-1'></i><FormattedMessage {...M.starterUseTemplate} />
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        </>
    )
}
